import { useEffect, useState } from "react";
import FlagReminderCard from "./FlagReminders/FlagReminderCard"
import { trainingAPI } from "@api/TrainingAPI";
import { IFlagReminderData, IFocusedMetrics } from "./FlagReminders/constants";
import { Tooltip } from "antd";
import { PremiumFeatureDisabled } from "@components/Common/PremiumFeatureDisabled";
import { useDispatch, useSelector } from "react-redux";
import { getUserDefinedFlags } from "@store/actions/userDefinedFlags.actions";
import { useAuth0 } from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import BlurFlagReminders from "./flag-reminders.png";
import { PermissionedButton, useUserPermissions } from "@components/PermissionedComponents";
import { AdminNotice } from "./AdminNotice";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";

export const FlagReminders = () => {
    const [flagReminderData, setFlagReminderData] = useState<IFlagReminderData[]>([]);
    const [showNewFlagReminder, setShowNewFlagReminder] = useState<boolean>(false)
    const [refetchData, setRefetchData] = useState<boolean>(false)
    const [focusedMetrics, setFocusedMetrics] = useState<IFocusedMetrics[]>([])

    const dispatch = useDispatch<any>();

    const {
        info: {
            flag_reminder_enabled,
            user_defined_flags_enabled,
            permissions_manager_list: permissionsManagerList
        }
    } = useSelector(({ team }) => team);
    const userPermissions = useUserPermissions();

    const { user } = useAuth0();
    const isStaff = isStaffEmail(user?.email);
    const hasFlagReminderPermission = userPermissions?.AddFlagReminder || false;
    const userDefinedFlagsEnabled = (user_defined_flags_enabled ?? false) || isStaff;

    const canViewFlagReminders =
        (flag_reminder_enabled || isStaff) && !(flag_reminder_enabled && !hasFlagReminderPermission);

    const loadFlagReminders = async () => {
        try {
            setFlagReminderData(await trainingAPI.getFlagReminderConfig() as IFlagReminderData[])
        } catch (e) {
            message.error("Error loading flag reminders")
        }
    }

    useEffect(() => {
        if (!canViewFlagReminders) return;
        (async () => {
            await loadFlagReminders()
            try {
                setFocusedMetrics(await trainingAPI.getFocusedMetrics() as IFocusedMetrics[])
            } catch (e) {
                message.error("Error loading metrics")
            }
        })()
    }, [canViewFlagReminders])

    useEffect(() => {
        if (userDefinedFlagsEnabled) dispatch(getUserDefinedFlags());
    }, [dispatch, userDefinedFlagsEnabled])

    useEffect(() => {
        if (refetchData) {
            loadFlagReminders()
            setRefetchData(false)
        }
    }, [refetchData])

    const clearNewReminder = () => {
        setShowNewFlagReminder(false)
        setRefetchData(true)
    }

    const flagReminderDefault: IFlagReminderData = {
        reminder_time: "12:00",
        lookback_hours: 0,
        metadata: {
            flag_types: [],
            recipients: [],
            individual_metrics: []
        },
        is_active: true,
        next_due_at: "",
    }

    if (!flag_reminder_enabled && !isStaff) {
        return (
            <PremiumFeatureDisabled
                bg_image={BlurFlagReminders}
                feature_name={"Flag Reminders"}
                show_navbar={false}
                info_text={<>
                    This is an elite feature that lets you create reminders for athlete flags. <br />
                    If you are interested in adding this to your platform, please contact your sales rep!</>}
            />
        )
    }

    if (flag_reminder_enabled && !hasFlagReminderPermission) {
        return (
            <div>
                You do not have permissions to view this page. <br />
                If you want to add/remove specific permissions to your account, please reach out to your team's permissions manager. <br /><br />
                Your team's Permissions Manager(s): <strong>{permissionsManagerList.map((user: any) => `${user.first_name} ${user.last_name}`).join(", ")}</strong>
            </div>
        )
    }

    return (
        <>
            {(!flag_reminder_enabled && isStaff) && (
                <div style={{ margin: '16px', maxWidth: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
                    <AdminNotice description={undefined} />
                </div>
            )}

            <p className="automatic-actions-timezone-text">
                All times are set to Eastern Time (EST)
            </p>

            <Tooltip
                title={
                    flagReminderData.length >= 1
                        ? 'Only one reminder allowed'
                        : ''
                }
            >
                <PermissionedButton permissionCheck="AddFlagReminder" style={{ marginBottom: '10px' }} onClick={() => { setShowNewFlagReminder(true) }} disabled={flagReminderData.length >= 1}>
                    {flagReminderData.length === 0 ? "Configure a Reminder" : "Add reminder"}
                </PermissionedButton>
            </Tooltip>

            {showNewFlagReminder &&
                <div style={{ marginBottom: '10px' }}>
                    <FlagReminderCard title={`Reminder (New)`} type="new" data={flagReminderDefault} clearNewReminder={clearNewReminder} focusedMetrics={focusedMetrics} />
                </div>
            }

            {flagReminderData.map((e, index) => {
                return (
                    <div style={{ marginBottom: '10px' }}>
                        <FlagReminderCard title={`Reminder`} type="existing" data={e} clearNewReminder={clearNewReminder} focusedMetrics={focusedMetrics} />
                    </div>
                )
            })}
        </>
    )
}

export default FlagReminders